import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import { Icon, IconName } from 'Components/UI';
import Details from './components/Details';
import Disciplines from './components/Disciplines';
import Users from './components/Users';
import Permissions from './components/Permissions';
import Nomenclature from './components/Nomenclature';
import Stages from './components/Stages';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import CTA from './components/CTA';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import {
  Container,
  Content,
  DrawerItem,
  DrawerLocal,
  LeftContent,
} from './styles';

const breadCrumbs: IBreadcrumbItem[] = [];

type ActiveDrawerType =
  'details' |
  'disciplines' |
  'users' |
  'permissions' |
  'nomenclature' |
  'stages' |
  'integrations';

interface IActiveDrawerItem {
  id: ActiveDrawerType;
  label: string;
  icon: IconName;
  badge?: string;
  disabled?: boolean;
  size?: number;
}

const EditObra: FC<Props> = ({
  isLoading,
  editLoading,
  isListStagesLoading,
  isListLibrariesLoading,
  Obra,
  userInfo,
  Disciplines: disciplines,
  tenantRoles,
  tenantSharedUsers,
  userLimitsActive,
  usersForLimitInvited,
  ObrasList,
  error,
  stages,
  libraries,
  tenantAllUsers,
}) => {
  const { t } = useTranslation('editObra');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const stagesFeatureFlag = useFeatureFlag('stages-feature');
  const stagesFeature = stagesFeatureFlag.enabled && stagesFeatureFlag.value === 'test';

  const csId = Number(params.constructionSiteId);
  const editId = params.editId as ActiveDrawerType;

  const firstLoad = useRef(true);
  const drawerTypes = useMemo(() => {
    const data: Record<ActiveDrawerType, IActiveDrawerItem> = {
      details: {
        id: 'details',
        label: t('drawer.details'),
        icon: 'collapse',
        size: 8,
      },
      disciplines: {
        id: 'disciplines',
        label: t('drawer.disciplines'),
        icon: 'menuContexto',
        size: 10,
      },
      users: {
        id: 'users',
        label: t('drawer.users'),
        icon: 'team',
      },
      permissions: {
        id: 'permissions',
        label: t('drawer.permissions'),
        icon: 'key',
      },
      nomenclature: {
        id: 'nomenclature',
        label: t('drawer.nomenclature'),
        icon: 'input',
      },
      stages: {
        id: 'stages',
        label: t('drawer.stages'),
        icon: 'phases',
        badge: !stagesFeature ? t('drawer.comingSoon') : undefined,
        disabled: !stagesFeature,
      },
      integrations: {
        id: 'integrations',
        label: t('drawer.integrations'),
        icon: 'bolt',
        badge: t('drawer.comingSoon'),
        disabled: true,
      },
    };
    return data;
  }, [stagesFeature, t]);

  const currentEditId = drawerTypes[editId];

  const toggleActiveDrawer = (title: ActiveDrawerType) => {
    navigate(`/obras/edit/${csId}/${title}`);
  };

  useEffect(() => {
    if (!Obra || isObjectEmpty(Obra)) {
      dispatch(ObraActions.getObra({ csId }));
    }
    if (firstLoad.current) {
      firstLoad.current = false;
      dispatch(ObraActions.getUsersConstructionSite(csId));
      dispatch(TenantActions.getUsers());
      dispatch(TenantActions.getTenantListUsersConstruction());
    }
  }, []);

  if (Obra) {
    breadCrumbs[0] = {
      href: `/obras/detail/${csId}`,
      description: Obra.Name,
    };
    breadCrumbs[1] = {
      href: `/obras/edit/${csId}/details`,
      description: t('breadcrumbEdit'),
    };
    breadCrumbs[2] = {
      href: `/obras/edit/${csId}/${currentEditId.id}`,
      description: currentEditId.label,
    };
  }

  return (
    <>
      <TabsShell
        ConstructionSiteId={csId}
        active={PathActiveEnum.edit}
        loading={editLoading}
      />
      <PageHeader name={t('pageTitle')} headerMarginLeft={16}>
        <BreadCrumb breadcrumbs={breadCrumbs} custom />
        <Container>
          <LeftContent>
            <DrawerLocal>
              {Object.values(drawerTypes).map((item) => (
                <DrawerItem
                  key={item.id}
                  active={editId === item.id}
                  disabled={item.disabled}
                  onClick={() => !item.disabled
                    ? toggleActiveDrawer(item.id)
                    : undefined
                  }
                >
                  <Icon
                    icon={item.icon}
                    className='iconDrawer'
                    customSize={item.size || 16}
                  />
                  <span className='textDrawer'>{item.label}</span>
                  {!!item.badge && (
                    <span className='badgeDrawer'>{item.badge}</span>
                  )}
                </DrawerItem>
              ))}
            </DrawerLocal>

            {editId === 'stages' && (
              <CTA />
            )}
          </LeftContent>

          <Content>
            {editId === 'details' && (
              <Details
                {...Obra}
                csId={csId}
                loading={isLoading}
              />
            )}
            {editId === 'disciplines' && (
              <Disciplines
                csId={csId}
                loading={isLoading}
                disciplines={Obra.Disciplines}
                disciplinesData={disciplines}
              />
            )}
            {editId === 'users' && (
              <Users
                csId={csId}
                loading={isLoading}
                tenantRoles={tenantRoles}
                tenantSharedUsers={tenantSharedUsers}
                invitedUsers={Obra.InvitedUsers}
                sharedUsers={Obra.SharedUsers}
                firstInvitedUsers={usersForLimitInvited}
                numberInvitedAllUsers={userLimitsActive?.QtyActiveUsers}
                userInfo={userInfo}
                tenantAllUsers={tenantAllUsers}
              />
            )}
            {editId === 'permissions' && (
              <Permissions
                csId={csId}
                loading={isLoading}
                invitedUsers={Obra.InvitedUsers}
                sharedUsers={Obra.SharedUsers}
                error={error}
              />
            )}
            {editId === 'stages' && (
              <Stages
                csId={csId}
                stages={stages}
                libraries={libraries}
                loading={isLoading}
                listStagesLoading={isListStagesLoading}
                listLibrariesLoading={isListLibrariesLoading}
                disciplines={Obra.Disciplines}
                versionConfigFk={Obra?.VersionConfigFk}
                obraVersionData={Obra?.VersionConfig}
                ValidateStagesInNomenclature={Obra.ValidateStagesInNomenclature}
                AllowOnlyStagesLinkedToDisciplinesInUpload={Obra.AllowOnlyStagesLinkedToDisciplinesInUpload}
                AllowOnlyStagesLinkedToDisciplinesInTopics={Obra.AllowOnlyStagesLinkedToDisciplinesInTopics}
              />
            )}
            {editId === 'nomenclature' && (
              <Nomenclature
                csId={csId}
                loading={isLoading}
                obraVersionData={Obra?.VersionConfig}
                versionConfigFk={Obra?.VersionConfigFk}
                hasValidateStagesInNomenclature={Obra.ValidateStagesInNomenclature}
              />
            )}
          </Content>
        </Container>
      </PageHeader>
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.obra,
  userInfo: state.auth.userInfo,
  globalLoading: state.activities.externalGlobalLoading,
  currentTenant: state.tenant.currentListTenant,
  tenantRoles: state.tenant?.TenantRoles || [],
  tenantSharedUsers: state.tenant?.Users || [],
  userLimitsActive: state.auth.userLimitsActive,
  isLoading: state.obra.newEditLoading,
  editLoading: state.obra.isLoading,
  isListStagesLoading: state.obra.listStagesLoading,
  isListLibrariesLoading: state.obra.listLibrariesLoading,
  tenantAllUsers: state.tenant.UsersWithConstruction,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(EditObra);
